import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { CheckSquare, ArrowRight, Smartphone, Palette, GripVertical, Search, RotateCw, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

const themeShowcase = [
  { key: 'prime', name: 'Prime', color: '#3b82f6' },
  { key: 'inkpaper', name: 'Ink & Paper', color: '#d4a574' },
  { key: 'executive', name: 'Executive', color: '#eab308' },
  { key: 'frostedglass', name: 'Frosted Glass', color: '#94a3b8' },
  { key: 'forest', name: 'Forest Focus', color: '#4d7c0f' },
  { key: 'ocean', name: 'Ocean Depth', color: '#0891b2' },
  { key: 'floral', name: 'Spring Floral', color: '#e879a8' },
  { key: 'sunset', name: 'Sunset', color: '#f97316' },
];

const features = [
  {
    icon: <CheckSquare className="w-6 h-6" />,
    title: 'ABC Prioritization',
    desc: 'Organize tasks into A (High), B (Important), and C (No Rush) categories with a single click or double-tap.'
  },
  {
    icon: <GripVertical className="w-6 h-6" />,
    title: 'Drag & Drop',
    desc: 'Reorder tasks within categories or drag between A, B, and C to reprioritize on the fly.'
  },
  {
    icon: <CalendarDays className="w-6 h-6" />,
    title: 'Auto-Forward',
    desc: "Unfinished tasks automatically carry over to the next day. Never lose track of what matters."
  },
  {
    icon: <RotateCw className="w-6 h-6" />,
    title: 'Recurring Tasks',
    desc: 'Set tasks to repeat daily, weekly, or monthly so your routines stay on autopilot.'
  },
  {
    icon: <Search className="w-6 h-6" />,
    title: 'Powerful Search',
    desc: 'Instantly search across all your tasks and journal notes. Find anything in seconds.'
  },
  {
    icon: <Palette className="w-6 h-6" />,
    title: '8 Beautiful Themes',
    desc: 'From Executive to Frosted Glass to Sunset — pick a look that matches your style, in light or dark mode.'
  },
  {
    icon: <Smartphone className="w-6 h-6" />,
    title: 'Works Everywhere',
    desc: 'Fully responsive. Use it on your phone, tablet, or desktop — your tasks sync across all devices.'
  },
  {
    icon: <CheckSquare className="w-6 h-6" />,
    title: 'Journal & Notes',
    desc: "Built-in daily journal to capture thoughts, meeting notes, or anything that doesn't fit in a task."
  }
];

const ThemeCarousel = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setActive(i => (i + 1) % themeShowcase.length), []);
  const prev = useCallback(() => setActive(i => (i - 1 + themeShowcase.length) % themeShowcase.length), []);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [paused, next]);

  const current = themeShowcase[active];

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="theme-carousel"
    >
      {/* Theme selector pills */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {themeShowcase.map((t, i) => (
          <button
            key={t.key}
            onClick={() => setActive(i)}
            className="px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300"
            style={i === active
              ? { backgroundColor: t.color, color: '#fff', boxShadow: `0 4px 14px ${t.color}40` }
              : { backgroundColor: '#f1f5f9', color: '#64748b' }
            }
            data-testid={`theme-pill-${t.key}`}
          >
            {t.name}
          </button>
        ))}
      </div>

      {/* Screenshot display with laptop frame */}
      <div className="relative max-w-4xl mx-auto">
        {/* Nav arrows */}
        <button
          onClick={prev}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-20 w-10 h-10 bg-white rounded-full shadow-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:shadow-xl transition-all"
          data-testid="carousel-prev"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={next}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-20 w-10 h-10 bg-white rounded-full shadow-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:shadow-xl transition-all"
          data-testid="carousel-next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Laptop frame */}
        <div className="bg-slate-800 rounded-t-xl pt-2 px-2 shadow-2xl shadow-slate-300/30">
          <div className="flex items-center gap-1.5 px-3 pb-2">
            <div className="w-2 h-2 rounded-full bg-red-400/80"></div>
            <div className="w-2 h-2 rounded-full bg-yellow-400/80"></div>
            <div className="w-2 h-2 rounded-full bg-green-400/80"></div>
            <div className="flex-1 mx-6">
              <div className="bg-slate-700 rounded-md h-5 flex items-center justify-center">
                <span className="text-[10px] text-slate-400 font-mono tracking-wide">taskpriority.net</span>
              </div>
            </div>
          </div>
          {/* Image container with crossfade */}
          <div className="relative overflow-hidden">
            {themeShowcase.map((t, i) => (
              <img
                key={t.key}
                src={`/theme-${t.key}.jpg`}
                alt={`${t.name} theme`}
                className="w-full block transition-opacity duration-700 ease-in-out"
                style={{
                  opacity: i === active ? 1 : 0,
                  position: i === active ? 'relative' : 'absolute',
                  top: 0,
                  left: 0,
                }}
              />
            ))}
          </div>
        </div>
        {/* Laptop base */}
        <div className="bg-gradient-to-b from-slate-700 to-slate-600 h-2 rounded-b-sm mx-[-2%]"></div>
        <div className="bg-gradient-to-b from-slate-600 to-slate-500 h-3 rounded-b-xl mx-[-4%]"></div>
      </div>

      {/* Theme name label */}
      <div className="text-center mt-6">
        <span
          className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold text-white transition-all duration-500"
          style={{ backgroundColor: current.color }}
        >
          {current.name}
        </span>
        <div className="flex justify-center gap-1.5 mt-4">
          {themeShowcase.map((t, i) => (
            <button
              key={t.key}
              onClick={() => setActive(i)}
              className="w-2 h-2 rounded-full transition-all duration-300"
              style={{
                backgroundColor: i === active ? themeShowcase[active].color : '#cbd5e1',
                transform: i === active ? 'scale(1.3)' : 'scale(1)',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-lg border-b border-slate-100 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 p-1.5 rounded-lg">
              <CheckSquare className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg sm:text-xl font-bold text-slate-900">TaskPriority Prime</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-600 hover:text-slate-900" data-testid="nav-login-btn">
              Log In
            </Button>
            <Button onClick={() => navigate('/login?signup=true')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="nav-signup-btn">
              Sign Up Free
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
              Simple.<br />
              Prioritized.<br />
              <span className="text-blue-600">Done.</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-lg">
              Stop overthinking your to-do list. TaskPriority Prime gives you a clean ABC system to focus on what matters most — every single day.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate('/login?signup=true')}
                className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-8 text-base rounded-xl shadow-lg shadow-blue-200"
                data-testid="hero-signup-btn"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="h-12 px-8 text-base rounded-xl border-slate-300"
                data-testid="hero-login-btn"
              >
                I already have an account
              </Button>
            </div>
            <p className="mt-4 text-sm text-slate-400">No credit card required. Free to use.</p>
          </div>
          <div className="relative pb-10" data-testid="hero-mockup">
            {/* Laptop frame */}
            <div className="relative mx-auto max-w-xl lg:max-w-none">
              {/* Screen bezel */}
              <div className="bg-slate-800 rounded-t-xl pt-2 px-2 pb-0 shadow-2xl shadow-slate-400/20">
                {/* Traffic lights + URL bar */}
                <div className="flex items-center gap-1.5 px-3 pb-2">
                  <div className="w-2 h-2 rounded-full bg-red-400/80"></div>
                  <div className="w-2 h-2 rounded-full bg-yellow-400/80"></div>
                  <div className="w-2 h-2 rounded-full bg-green-400/80"></div>
                  <div className="flex-1 mx-6">
                    <div className="bg-slate-700 rounded-md h-5 flex items-center justify-center">
                      <span className="text-[10px] text-slate-400 font-mono tracking-wide">taskpriority.net</span>
                    </div>
                  </div>
                </div>
                {/* Screenshot */}
                <img src="/mockup-desktop.jpg" alt="TaskPriority Prime dashboard" className="w-full block" />
              </div>
              {/* Laptop hinge */}
              <div className="bg-gradient-to-b from-slate-700 to-slate-600 h-2 rounded-b-sm mx-[-2%]"></div>
              {/* Laptop base */}
              <div className="bg-gradient-to-b from-slate-600 to-slate-500 h-3 rounded-b-xl mx-[-4%]"></div>
            </div>

            {/* Phone mockup floating overlay */}
            <div className="absolute -bottom-4 right-0 sm:-right-4 w-24 sm:w-32 lg:w-36 z-10">
              <div className="bg-slate-900 rounded-[1rem] sm:rounded-[1.2rem] p-1 sm:p-1.5 shadow-2xl shadow-black/40 border border-slate-700/80">
                {/* Notch */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-8 sm:w-12 h-1 sm:h-1.5 bg-slate-800 rounded-full z-10"></div>
                <img src="/mockup-mobile.jpg" alt="TaskPriority Prime mobile view" className="w-full rounded-[0.6rem] sm:rounded-[0.8rem]" />
                {/* Home bar */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-8 sm:w-10 h-1 bg-slate-600 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof bar */}
      <section className="bg-slate-50 border-y border-slate-100 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-500 text-sm sm:text-base">
            Trusted by professionals who value simplicity over complexity
          </p>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" id="features">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Everything you need. Nothing you don&apos;t.</h2>
            <p className="mt-4 text-lg text-slate-500 max-w-2xl mx-auto">
              Built for people who want to get things done without learning a complicated system.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-xl border border-slate-100 p-6 hover:shadow-lg hover:border-slate-200 transition-all duration-300 group">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                  {f.icon}
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-slate-900 py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">How it works</h2>
            <p className="mt-4 text-lg text-slate-400">Three steps. That&apos;s it.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Add your tasks', desc: 'Type it in, pick a category, and go. Tasks default to today.' },
              { step: '2', title: 'Prioritize with ABC', desc: 'A is urgent, B is important, C can wait. Drag to reorder anytime.' },
              { step: '3', title: 'Check them off', desc: "Complete tasks as you go. Unfinished ones auto-forward to tomorrow." }
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-16 h-16 mx-auto bg-blue-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mb-4">
                  {s.step}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{s.title}</h3>
                <p className="text-slate-400">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Themes carousel */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" data-testid="themes-carousel-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Make it yours</h2>
            <p className="mt-4 text-lg text-slate-500 max-w-2xl mx-auto">
              Choose from 8 distinct themes — each with light and dark modes. From the clean lines of Prime to the warm glow of Sunset, there&apos;s a look for every style.
            </p>
          </div>
          <ThemeCarousel />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-600 py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Ready to get organized?</h2>
          <p className="mt-4 text-lg text-blue-100">
            Join TaskPriority Prime today. It&apos;s free, it&apos;s simple, and it works.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/login?signup=true')}
              className="bg-white text-blue-600 hover:bg-blue-50 h-12 px-8 text-base rounded-xl font-semibold"
              data-testid="cta-signup-btn"
            >
              Sign Up Free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-white/40 text-white hover:bg-white/10 h-12 px-8 text-base rounded-xl"
              data-testid="cta-login-btn"
            >
              Log In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-100 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 p-1 rounded-md">
              <CheckSquare className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-semibold text-slate-600">TaskPriority Prime</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <a href="/notes" className="hover:text-slate-700 transition-colors">About</a>
            <a href="/privacy" className="hover:text-slate-700 transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-slate-700 transition-colors">Terms of Service</a>
            <span>&copy; 2026 Robert Farrah</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
